import { addDays, format, formatISO, isSameDay, startOfWeek } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { MONDAY, MONDAY_OF_WEEK } from '@/util/constants';
import { updateUrlParams } from '@/util/dashboard';

// Calendar navigation for BOTH views (GRAVY-10329).
//
// The Day view is the dashboard's default: an Ops shift is worked a day at a
// time, and the week grid is the overview you step out to. So `view` is part of
// the navigation state rather than a flag the tab reads separately — the step
// size, the anchor date, the label and the window span all follow from it.
//
// ## Persistence
//
// Both live in the URL (`?view=`, `?sd=`), like the active tab (`?tab=`). That
// survives a reload, a deep link and a browser Back, and keeps one source of
// truth. It does NOT survive a brand-new session with a bare URL, which the
// ticket allows ("across sessions or reloads").
//
// ## Anchoring
//
// Week snaps to Monday, because the grid IS a Monday-anchored week. Day must
// not snap — the anchor is the day itself. Switching Day -> Week snaps to the
// week containing the day, and Week -> Day picks today when today falls inside
// the visible week and the week's Monday otherwise, so the view change lands
// somewhere the admin was already looking.

export type CalendarView = 'day' | 'week';

const DAY_SPAN = 1;
const WEEK_SPAN = 7;

const asISO = (d: Date) => formatISO(d, { representation: 'date' });

/** "Jun 22 – 28, 2026" within a month, "Jun 29 – Jul 5, 2026" across months. */
const formatRange = (start: Date): string => {
  const end = addDays(start, 6);
  const year = format(end, 'yyyy');
  if (format(start, 'MMM') === format(end, 'MMM')) {
    return `${format(start, 'MMM d')} – ${format(end, 'd')}, ${year}`;
  }
  return `${format(start, 'MMM d')} – ${format(end, 'MMM d')}, ${year}`;
};

/** "Today, Jul 29" on today, "Tue, Jul 30" otherwise — the design's wording. */
const formatDay = (d: Date, today: Date): string =>
  isSameDay(d, today)
    ? `Today, ${format(d, 'MMM d')}`
    : format(d, 'EEE, MMM d');

const readView = (): CalendarView => {
  const q = new URLSearchParams(window.location.search).get('view');
  // Day is the default, so an absent or unrecognised value lands there rather
  // than on the week grid.
  return q === 'week' ? 'week' : 'day';
};

/** The `?sd=` date as written, WITHOUT the week snap `getInitialStartDate` does. */
const readAnchor = (view: CalendarView, today: Date): Date => {
  const q = new URLSearchParams(window.location.search).get('sd');
  const parsed = q ? new Date(`${q}T00:00:00`) : null;
  const valid = parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;

  if (view === 'week') {
    return startOfWeek(valid ?? today, { weekStartsOn: MONDAY });
  }
  return valid ?? today;
};

const useCalendarNav = () => {
  const today = useMemo(() => new Date(), []);
  const [view, setViewState] = useState<CalendarView>(readView);
  const [anchor, setAnchor] = useState<Date>(() =>
    readAnchor(readView(), today),
  );

  const commit = useCallback((nextView: CalendarView, nextAnchor: Date) => {
    const settled =
      nextView === 'week'
        ? startOfWeek(nextAnchor, { weekStartsOn: MONDAY })
        : nextAnchor;
    setViewState(nextView);
    setAnchor(settled);
    updateUrlParams({ view: nextView, sd: asISO(settled) });
  }, []);

  const setView = useCallback(
    (next: CalendarView) => {
      if (next === view) return;
      if (next === 'week') {
        commit('week', anchor);
        return;
      }
      // Week -> Day: prefer today when it is inside the week being looked at,
      // so the switch lands on the day the admin most likely wants rather than
      // on a Monday three weeks out.
      const weekStart = startOfWeek(anchor, { weekStartsOn: MONDAY });
      const inWeek = today >= weekStart && today < addDays(weekStart, 7);
      commit('day', inWeek ? today : weekStart);
    },
    [anchor, commit, today, view],
  );

  // Publish the resolved view and date ON MOUNT, not only when something is
  // clicked. Without this the URL carries neither until the first navigation,
  // so a freshly opened dashboard cannot be shared or reloaded back to — which
  // is the persistence the ticket asks for, and what `[C-12]` caught.
  //
  // `replaceState` (via updateUrlParams), so it adds no history entry and
  // browser Back still leaves the page.
  useEffect(() => {
    updateUrlParams({ view, sd: asISO(anchor) });
    // Mount only: every later change goes through `commit`, which writes both.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const step = view === 'day' ? DAY_SPAN : WEEK_SPAN;
  const goPrev = useCallback(
    () => commit(view, addDays(anchor, -step)),
    [anchor, commit, step, view],
  );
  const goNext = useCallback(
    () => commit(view, addDays(anchor, step)),
    [anchor, commit, step, view],
  );
  const goToday = useCallback(
    () => commit(view, view === 'day' ? today : MONDAY_OF_WEEK),
    [commit, today, view],
  );

  // Drives the Today control's disabled state: the design greys it out when it
  // would do nothing.
  const isOnToday =
    view === 'day'
      ? isSameDay(anchor, today)
      : isSameDay(
          startOfWeek(anchor, { weekStartsOn: MONDAY }),
          startOfWeek(today, { weekStartsOn: MONDAY }),
        );

  return {
    view,
    setView,
    startDate: anchor,
    startDateISO: asISO(anchor),
    /** Days the request should span — the ONE thing the API needs to differ. */
    spanDays: view === 'day' ? DAY_SPAN : WEEK_SPAN,
    rangeLabel: view === 'day' ? formatDay(anchor, today) : formatRange(anchor),
    isOnToday,
    goPrev,
    goNext,
    goToday,
  };
};

export default useCalendarNav;
